import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorDeApi, leerMapa, type MapaDeEsquemas, type PuntajeDeEsquema } from './api';
import { claseDeColor, coloresPorDimension, esquemasPrincipales } from './dimensiones';
import { Anillo, Barra } from './graficos';
import { urlParaEmbeber } from './video';

/**
 * El mapa de esquemas, tal como lo ve quien acaba de responder el test — HU-09.
 *
 * QUÉ MOMENTO ES ESTE. Alguien acaba de contestar setenta y cinco frases sobre
 * su vida y está esperando a que le digan algo. Es el instante de más atención de
 * todo el producto y probablemente el único en que va a leer despacio: lo que
 * aparezca aquí es lo que va a recordar del sistema.
 *
 * POR QUÉ SE VE COMO SE VE (el orden lo pidió la psicóloga en agosto de 2026:
 * explicación → lectura → resumen → detalle):
 *
 *   - **Primero el encuadre, después los números.** Ver «5,6» sobre uno mismo sin
 *     nada delante se lee como una nota. El párrafo que lo precede lo escribe la
 *     psicóloga, y sin él esta pantalla no se muestra (lo decide el servidor).
 *   - **Después la lectura que enhebra el resultado** (el gancho), antes que
 *     cualquier número: es la frase que convierte los puntajes en algo sobre
 *     esta persona y no sobre una escala.
 *   - **Después los cuatro esquemas principales, cada uno en su círculo.** Su
 *     puntaje en grande y el anillo hasta ahí. Del resumen al detalle: antes
 *     fueron la rosa y luego las cinco puertas (un círculo por área); el
 *     cliente pidió en octubre de 2026 que los círculos fueran de esquemas.
 *   - **Los esquemas altos después y con sitio.** Son cuatro o cinco, no quince.
 *     Una tabla de quince filas hace que nadie lea ninguna.
 *   - **El perfil completo va debajo y en pequeño.** Está porque tiene que estar
 *     —es su resultado entero— pero no compite con lo que importa.
 *   - **Cada tarjeta termina en la necesidad, no en el problema.** Es lo que
 *     convierte un puntaje alto en algo que se puede mirar sin sentirse roto.
 *
 * NI UNA PALABRA CLÍNICA ESCRITA AQUÍ. Los nombres de los esquemas, las
 * descripciones, el encuadre y el pie vienen del catálogo del panel. Lo
 * que sí se escribe en este archivo son rótulos de interfaz —«Tu perfil
 * completo»—, que no dicen nada sobre nadie.
 *
 * NI UN `style` EN ATRIBUTO. La CSP del servidor es `style-src 'self'`: los
 * dibujos son SVG por eso —el tamaño va en atributos, que no son estilos— y el
 * color va por clase. Ver `graficos.tsx`.
 */

/** Cada cuánto se vuelve a preguntar por el párrafo mientras se escribe. */
const ESPERA_ENTRE_INTENTOS_MS = 2500;

/**
 * Cuántas veces se pregunta antes de darlo por perdido.
 *
 * Son unos veinte segundos. Pasado eso, o el modelo se atascó o el proceso que
 * lo pidió murió, y seguir enseñando «un momento…» para siempre es peor que
 * enseñar el mapa sin párrafo: el mapa está completo sin él.
 */
const INTENTOS_MAXIMOS = 8;

const numero = new Intl.NumberFormat('es-CO', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const fecha = new Intl.DateTimeFormat('es-CO', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

type Estado =
  | { fase: 'cargando' }
  | { fase: 'listo'; mapa: MapaDeEsquemas }
  /** No se pudo hablar con el servidor. No se pinta nada: lo decide quien monta. */
  | { fase: 'fallo' };

/**
 * Avisa cuando el elemento entra en pantalla, para que el perfil se dibuje al
 * llegar a él y no mientras está fuera de la vista.
 *
 * SI NO HAY `IntersectionObserver`, SE DA POR VISTO. Es la diferencia entre una
 * animación que no ocurre y un resultado que no se ve: si esto fallara callado,
 * las barras se quedarían encogidas y la persona vería su perfil en blanco.
 */
function useVisible<T extends Element>(): [(nodo: T | null) => void, boolean] {
  const [visible, setVisible] = useState(!('IntersectionObserver' in window));
  const vigia = useRef<IntersectionObserver | null>(null);

  /**
   * VA COMO CALLBACK Y NO COMO `useEffect` CON UN `ref`, y esto ya falló una
   * vez: cuando esta pantalla se monta todavía dice «estamos preparando tu
   * mapa», así que la lista no existe. Un efecto con dependencias vacías corre
   * en ese momento, no encuentra nodo que vigilar y no se reintenta nunca. Así
   * se engancha cuando el elemento aparece de verdad, sea cuando sea.
   */
  const referencia = useCallback((nodo: T | null) => {
    vigia.current?.disconnect();
    if (!nodo || !('IntersectionObserver' in window)) return;

    const observador = new IntersectionObserver(
      (entradas) => {
        // Una vez visto, se deja de mirar: esto no se vuelve a esconder.
        if (entradas.some((e) => e.isIntersecting)) {
          setVisible(true);
          observador.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    );

    observador.observe(nodo);
    vigia.current = observador;
  }, []);

  return [referencia, visible];
}

/**
 * `respaldo` es lo que se pinta cuando no hay mapa que enseñar, y va como prop
 * en vez de un callback porque los tres motivos —sin catálogo, sin los textos de
 * Ángela, o sin conexión— acaban en lo mismo: la pantalla de cierre de siempre.
 * Hoy es el caso normal, no la excepción.
 */
export function Mapa({ respaldo }: { respaldo: React.ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });
  const intentos = useRef(0);
  const [perfil, perfilVisible] = useVisible<HTMLUListElement>();

  useEffect(() => {
    let vivo = true;
    let temporizador: ReturnType<typeof setTimeout> | undefined;

    const preguntar = async (): Promise<void> => {
      try {
        const mapa = await leerMapa();
        if (!vivo) return;
        setEstado({ fase: 'listo', mapa });

        /**
         * Se vuelve a preguntar solo mientras el párrafo se esté escribiendo. El
         * resto del mapa ya está en pantalla desde el primer intento: esto no es
         * una espera, es algo que llega después.
         */
        if (mapa.disponible && mapa.ganchoEstado === 'pendiente') {
          intentos.current += 1;
          if (intentos.current < INTENTOS_MAXIMOS) {
            temporizador = setTimeout(() => void preguntar(), ESPERA_ENTRE_INTENTOS_MS);
          }
        }
      } catch (error) {
        if (!vivo) return;
        // Un fallo de red al pedir el mapa no puede acabar en una pantalla de
        // error: la persona ya terminó, y su cierre por WhatsApp va igual.
        if (error instanceof ErrorDeApi) {
          setEstado({ fase: 'fallo' });
          return;
        }
        throw error;
      }
    };

    void preguntar();

    return () => {
      vivo = false;
      if (temporizador) clearTimeout(temporizador);
    };
  }, []);

  if (estado.fase === 'cargando') {
    return (
      <div className="mapa mapa--cargando">
        <img
          className="pantalla__marca pantalla__marca--latiendo"
          src="/marca/logo-sobre-claro.svg"
          alt="Ser Prana"
        />
        <p>Estamos preparando tu mapa…</p>
      </div>
    );
  }

  if (estado.fase === 'fallo' || !estado.mapa.disponible) return <>{respaldo}</>;

  const { mapa } = estado;
  const elevados = mapa.puntajes.filter((p) => p.elevado);
  const colores = coloresPorDimension(mapa.puntajes);
  const video = mapa.videoUrl === null ? null : urlParaEmbeber(mapa.videoUrl);

  return (
    <article className="mapa">
      <img className="marca" src="/marca/logo-sobre-claro.svg" alt="Ser Prana" />

      <header className="mapa__portada">
        <h1 className="mapa__titulo">{mapa.titulo}</h1>
        <p className="mapa__fecha">{fecha.format(new Date(mapa.generadoEn))}</p>
      </header>

      {mapa.encuadre !== null && <p className="mapa__encuadre">{mapa.encuadre}</p>}

      <Gancho texto={mapa.gancho} estado={mapa.ganchoEstado} />

      <EsquemasPrincipales puntajes={mapa.puntajes} escala={mapa.escala} colores={colores} />

      {elevados.length > 0 && (
        <section className="mapa__seccion">
          <h2 className="mapa__rotulo">
            {elevados.length === 1 ? 'El que más pesa hoy' : 'Los que más pesan hoy'}
          </h2>

          <div className="tarjetas">
            {elevados.map((esquema) => (
              <TarjetaDeEsquema
                key={esquema.nombre}
                esquema={esquema}
                escala={mapa.escala}
                umbral={mapa.umbralElevado}
                color={claseDeColor(esquema, colores)}
              />
            ))}
          </div>
        </section>
      )}

      {mapa.puntajes.length > 0 && (
        <section className="mapa__seccion">
          <h2 className="mapa__rotulo">Tu perfil completo</h2>
          <p className="mapa__nota">
            Los {mapa.puntajes.length} de mayor a menor. La línea marca dónde empieza lo que
            conviene mirar. Tener un puntaje bajo en alguno no es mejor ni peor: es información
            sobre ti.
          </p>

          <ul
            className={`perfil${perfilVisible ? ' perfil--visible' : ''} escalonado`}
            ref={perfil}
          >
            {mapa.puntajes.map((esquema) => (
              <li
                key={esquema.nombre}
                className={`perfil__fila ${claseDeColor(esquema, colores)}${
                  esquema.elevado ? ' perfil__fila--alta' : ''
                }`}
              >
                <span className="perfil__nombre">{esquema.nombre}</span>
                <Barra valor={esquema.puntaje} escala={mapa.escala} umbral={mapa.umbralElevado} />
                <span className="perfil__puntaje">{numero.format(esquema.puntaje)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* El video que grabó la psicóloga, al final del recorrido: después de
          los números y antes del pie de no-diagnóstico. Si no hay enlace —o el
          enlace no da para un video— la sección entera desaparece, sin hueco
          ni disculpa. */}
      {video !== null && (
        <section className="mapa__seccion mapa__video">
          <h2 className="mapa__rotulo">Un mensaje para ti</h2>
          <div className="video">
            <iframe
              className="video__marco"
              src={video}
              title="Un mensaje para ti"
              allow="fullscreen; picture-in-picture"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          </div>
        </section>
      )}

      {mapa.cierre !== null && <p className="mapa__cierre">{mapa.cierre}</p>}
    </article>
  );
}

/**
 * Los cuatro esquemas principales, cada uno en su círculo. En octubre de 2026
 * reemplazaron a las cinco puertas (un círculo por área), a pedido del cliente.
 *
 * CADA CÍRCULO ES UN ESQUEMA: su puntaje en el centro, el anillo hasta ahí, y
 * su nombre debajo. Debajo del nombre va el área en pequeño, porque es lo que
 * dice el color: sin ella, el color del anillo no significaría nada.
 *
 * VAN LOS CUATRO IGUALES, en una rejilla de dos por dos: ninguno ocupa el
 * ancho entero. Con cuatro, uno destacado arriba dejaría al último solo en su
 * fila.
 */
function EsquemasPrincipales({
  puntajes,
  escala,
  colores,
}: {
  puntajes: PuntajeDeEsquema[];
  escala: { minimo: number; maximo: number };
  colores: Map<string, number>;
}) {
  const principales = esquemasPrincipales(puntajes);
  if (principales.length === 0) return null;

  return (
    <div className="mapa__puertas">
      <div className="puertas escalonado">
        {principales.map((esquema) => (
          <div key={esquema.nombre} className={`puerta ${claseDeColor(esquema, colores)}`}>
            <Anillo valor={esquema.puntaje} escala={escala} />
            <h3 className="puerta__nombre">{esquema.nombre}</h3>
            {esquema.dimensionNombre !== null && (
              <p className="puerta__cuenta">{esquema.dimensionNombre}</p>
            )}
          </div>
        ))}
      </div>
      <p className="mapa__pie-puertas">
        {principales.length === 1
          ? 'Tu esquema con el puntaje más alto.'
          : `Tus ${String(principales.length)} esquemas con el puntaje más alto.`}{' '}
        El anillo marca hasta dónde llega cada uno en la escala.
      </p>
    </div>
  );
}

/**
 * El párrafo que enhebra los esquemas altos.
 *
 * TIENE TRES ESTADOS Y LOS TRES IMPORTAN. Mientras se escribe se dice que se
 * está escribiendo, porque un hueco que aparece de la nada veinte segundos
 * después parece un fallo; si no va a haber, no se deja ni el hueco ni una
 * disculpa, porque el mapa está completo sin él y nadie echa de menos algo que
 * no sabe que existía.
 */
function Gancho({ texto, estado }: { texto: string | null; estado: string }) {
  if (estado === 'pendiente') {
    return (
      <div className="gancho gancho--escribiendo" aria-live="polite">
        <span className="gancho__puntos" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <p className="gancho__espera">Estamos leyendo lo que tus respuestas tienen en común…</p>
      </div>
    );
  }

  if (texto === null) return null;

  return (
    <div className="gancho" aria-live="polite">
      <p className="gancho__texto">{texto}</p>
    </div>
  );
}

/**
 * Un esquema alto, con sitio para leerse.
 *
 * EL PUNTAJE VA GRANDE PERO NO ES EL PROTAGONISTA: encima está el nombre y
 * debajo lo que significa. Un número solo, en grande, se lee como una
 * calificación, y esto no califica a nadie.
 *
 * TERMINA EN LA NECESIDAD. Es la vuelta que da todo el método: un esquema alto
 * no es un defecto, es la marca de algo que hizo falta y no llegó. Si el
 * catálogo no la trae, la línea no se pinta.
 */
function TarjetaDeEsquema({
  esquema,
  escala,
  umbral,
  color,
}: {
  esquema: PuntajeDeEsquema;
  escala: { minimo: number; maximo: number };
  umbral: number;
  color: string;
}) {
  return (
    <div className={`tarjeta ${color}`}>
      {esquema.dimensionNombre !== null && (
        <p className="tarjeta__dimension">{esquema.dimensionNombre}</p>
      )}

      <h3 className="tarjeta__nombre">{esquema.nombre}</h3>

      <div className="tarjeta__medida">
        <Barra valor={esquema.puntaje} escala={escala} umbral={umbral} />
        <p className="tarjeta__puntaje">
          {numero.format(esquema.puntaje)}
          <span className="tarjeta__sobre"> / {escala.maximo}</span>
        </p>
      </div>

      {esquema.descripcion !== null && <p className="tarjeta__texto">{esquema.descripcion}</p>}

      {esquema.necesidad !== null && (
        <p className="tarjeta__necesidad">
          <span className="tarjeta__necesidad-rotulo">Lo que necesitabas</span>
          {esquema.necesidad}
        </p>
      )}
    </div>
  );
}
